import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { DashboardLayout } from './DashboardLayout';
import { PurchasePriceRoute } from './components/PurchasePriceRoute';
import { MobileLayout } from './layouts/MobileLayout';
import { POSTerminalLayout } from './layouts/POSTerminalLayout';
import WarehouseLayout from './pages/warehouse/WarehouseLayout';
import ClientsLayout from './pages/clients/ClientsLayout';
import LoginPage from './pages/LoginPage';
import ProtectedRoute from './components/ProtectedRoute';
import CommandPalette from './components/ui/CommandPalette';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './components/theme-provider';
import { Toaster } from 'react-hot-toast';
import { useAuth } from './context/AuthContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SocketProvider } from './context/SocketContext';
import { LanguageProvider } from './context/LanguageContext';
import { SettingsSync } from './components/SettingsSync';
import { GlobalLoader } from './components/GlobalLoader';
import { NetworkStatus } from './components/ui/NetworkStatus';
import { AppUpdateNotice } from './components/AppUpdateNotice';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Pages (Lazy Loaded)
const MaterialMovementPage = lazy(() => import('./pages/analytics/MaterialMovementPage'));
const Dashboard = lazy(() => import('./pages/DashboardPage'));
const OrdersPage = lazy(() => import('./pages/OrdersPage'));
const OrderFormPage = lazy(() => import('./pages/OrderFormPage'));
const OrderImportPage = lazy(() => import('./pages/OrderImportPage'));
const ClientsWholesalePage = lazy(() => import('./pages/ClientsWholesalePage'));
const ClientsRetailPage = lazy(() => import('./pages/ClientsRetailPage'));
const LocationsPage = lazy(() => import('./pages/inventory/LocationsPage'));
const ProductsPage = lazy(() => import('./pages/inventory/ProductsPage'));
const ProductDetailPage = lazy(() => import('./pages/inventory/ProductDetailPage'));
const PricingPage = lazy(() => import('./pages/inventory/PricingPage'));
const StocksPage = lazy(() => import('./pages/inventory/StocksPage'));
const CategoryPage = lazy(() => import('./pages/reference/CategoriesPage'));
const BrandsPage = lazy(() => import('./pages/reference/BrandsPage'));
const UnitsPage = lazy(() => import('./pages/reference/UnitsPage'));
const PriceTypesPage = lazy(() => import('./pages/reference/PriceTypesPage'));
const SupplierPage = lazy(() => import('./pages/reference/SuppliersPage'));
const PaymentTypePage = lazy(() => import('./pages/reference/PaymentTypesPage'));
const ExpenseCategoryPage = lazy(() => import('./pages/reference/ExpenseCategoriesPage'));
const DistrictsPage = lazy(() => import('./pages/reference/DistrictsPage'));
const AccountsPage = lazy(() => import('./pages/reference/AccountsPage'));
const FinancePage = lazy(() => import('./pages/finance/FinancePage'));
const InstallmentsPage = lazy(() => import('./pages/finance/InstallmentsPage'));
const DebtsPage = lazy(() => import('./pages/finance/DebtsPage'));
const BranchesPage = lazy(() => import('./pages/reference/BranchesPage'));
const CarBrandsPage = lazy(() => import('./pages/CarBrandsPage'));
const ClientCategoriesPage = lazy(() => import('./pages/reference/ClientCategoriesPage'));
const CarModelsPage = lazy(() => import('./pages/CarModelsPage'));
const RefusalReasonsPage = lazy(() => import('./pages/reference/RefusalReasonsPage'));
const EquipmentTypesPage = lazy(() => import('./pages/reference/EquipmentTypesPage'));
const ReferencesPage = lazy(() => import('./pages/reference/ReferencesPage'));
const CurrenciesMainPage = lazy(() => import('./pages/reference/CurrenciesMainPage'));
const EmployeesPage = lazy(() => import('./pages/hr/EmployeesPage'));
const AttendancePage = lazy(() => import('./pages/hr/AttendancePage'));
const TasksPage = lazy(() => import('./pages/TasksPage'));
const TransfersPage = lazy(() => import('./pages/warehouse/TransfersPage'));
const PurchasesPage = lazy(() => import('./pages/warehouse/PurchasesPage'));
const WarehouseDashboardPage = lazy(() => import('./pages/warehouse/WarehouseDashboardPage'));
const WriteOffsPage = lazy(() => import('./pages/warehouse/WriteOffsPage'));
const AdjustmentsPage = lazy(() => import('./pages/warehouse/AdjustmentsPage'));
const ReturnsPage = lazy(() => import('./pages/returns/ReturnsPage'));
const LogisticsPage = lazy(() => import('./pages/LogisticsPage'));
const KpiPage = lazy(() => import('./pages/analytics/KpiPage'));
const SetKpiPlansPage = lazy(() => import('./pages/analytics/SetKpiPlansPage'));
const AnalyticsSelectionPage = lazy(() => import('./pages/analytics/AnalyticsSelectionPage'));
const ProductSalesPage = lazy(() => import('./pages/analytics/ProductSalesPage'));
const SalesAnalyticsPage = lazy(() => import('./pages/analytics/SalesAnalyticsPage'));
const ClientEquipmentPage = lazy(() => import('./pages/analytics/ClientEquipmentPage'));
const SystemSettingsPage = lazy(() => import('./pages/settings/SystemSettingsPage'));
const AuditLogPage = lazy(() => import('./pages/settings/AuditLogPage'));
const AccountantDashboardPage = lazy(() => import('./pages/analytics/AccountantDashboardPage'));
const RegionalDashboardPage = lazy(() => import('./pages/analytics/RegionalDashboardPage'));
const ProfileSettingsPage = lazy(() => import('./pages/settings/ProfileSettingsPage'));
const OrderDetailPage = lazy(() => import('./pages/OrderDetailPage'));
const ClientDetailPage = lazy(() => import('./pages/ClientDetailPage'));
const ClientFormPage = lazy(() => import('./pages/ClientFormPage'));
const ReportBuilderPage = lazy(() => import('./pages/analytics/ReportBuilderPage'));
const PnlPage = lazy(() => import('./pages/analytics/PnlPage'));
const AiChatPage = lazy(() => import('./pages/analytics/AiChatPage'));
const MaterialReportPage = lazy(() => import('./pages/analytics/MaterialReportPage'));
const SlowMovingGoodsPage = lazy(() => import('./pages/analytics/SlowMovingGoodsPage'));
const DirectorDashboardPage = lazy(() => import('./pages/analytics/DirectorDashboardPage'));
const VisitsPage = lazy(() => import('./pages/VisitsPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const ContractsPage = lazy(() => import('./pages/contracts/ContractsPage'));
const ContractDetailPage = lazy(() => import('./pages/contracts/ContractDetailPage'));
const VideoCampaignPage = lazy(() => import('./pages/VideoCampaignPage'));

// Stores
const StoresListPage = lazy(() => import('./pages/stores/StoresListPage'));
const StoreDetailPage = lazy(() => import('./pages/stores/StoreDetailPage'));

// POS
const POSTerminalPage = lazy(() => import('./pages/pos/POSTerminalPage'));

// Mobile Pages (Lazy Loaded)
const MobileOrdersPage = lazy(() => import('./pages/mobile/MobileOrdersPage'));
const MobileDashboardPage = lazy(() => import('./pages/mobile/MobileDashboardPage'));
const MobileVisitsPage = lazy(() => import('./pages/mobile/MobileVisitsPage'));
const MobileClientsPage = lazy(() => import('./pages/mobile/MobileClientsPage'));
const MobileStocksPage = lazy(() => import('./pages/mobile/MobileStocksPage'));
const MobileAnalyticsPage = lazy(() => import('./pages/mobile/MobileAnalyticsPage'));
const MobileNewOrderPage = lazy(() => import('./pages/mobile/MobileNewOrderPage'));
const MobileOrderViewPage = lazy(() => import('./pages/mobile/MobileOrderViewPage'));
const MobileClientDetailPage = lazy(() => import('./pages/mobile/MobileClientDetailPage'));
const MobileVisitDetailPage = lazy(() => import('./pages/mobile/MobileVisitDetailPage'));
const StorefrontSettingsPage = lazy(() => import('./pages/storefront/StorefrontSettingsPage'));
const StorefrontPodborPage = lazy(() => import('./pages/storefront/StorefrontPodborPage'));
const StorefrontDashboardPage = lazy(() => import('./pages/storefront/StorefrontDashboardPage'));
const StorefrontOrdersPage = lazy(() => import('./pages/storefront/StorefrontOrdersPage'));
const StorefrontHomePage = lazy(() => import('./pages/storefront/StorefrontHomePage'));
const StorefrontCategoriesPage = lazy(() => import('./pages/storefront/StorefrontCategoriesPage'));
const StorefrontProductPage = lazy(() => import('./pages/storefront/StorefrontProductPage'));
const StorefrontPagesPage = lazy(() => import('./pages/storefront/StorefrontPagesPage'));
const StorefrontBlogPage = lazy(() => import('./pages/storefront/StorefrontBlogPage'));
const StorefrontSeoPage = lazy(() => import('./pages/storefront/StorefrontSeoPage'));

const MobileTasksPage = lazy(() => import('./pages/mobile/MobileTasksPage'));
const MobileInstallmentsPage = lazy(() => import('./pages/mobile/MobileInstallmentsPage'));
const MobileAttendancePage = lazy(() => import('./pages/mobile/MobileAttendancePage'));

const MainLayout = () => {
  const { user } = useAuth();
  const role = user?.role?.toString().trim().toUpperCase();
  if (role === 'SALES_AGENT' || role === 'FORWARDER') {
    return <MobileLayout />;
  }
  return <DashboardLayout />;
};

const DefaultRedirect = () => {
  const { user } = useAuth();
  const role = user?.role?.toString().trim().toUpperCase();
  if (role === 'SALES_AGENT' || role === 'FORWARDER') return <Navigate to="/agent/dashboard" replace />;
  if (role === 'WAREHOUSE_MANAGER') return <Navigate to="/warehouse/dashboard" replace />;
  if (role === 'CHIEF_ACCOUNTANT' || role === 'ACCOUNTANT') return <Navigate to="/accountant/dashboard" replace />;
  if (role === 'REGIONAL_MANAGER') return <Navigate to="/regional/dashboard" replace />;
  if (role === 'DIRECTOR') return <Navigate to="/director/dashboard" replace />;
  if (role === 'CASHIER') return <Navigate to="/pos/terminal" replace />;
  return <Navigate to="/dashboard" replace />;
};

const AppRoutes = () => {
  return (
    <Suspense fallback={<GlobalLoader fullScreen={true} />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        <Route
          path="/"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DefaultRedirect />} />

          {/* General/Desktop Routes */}
          <Route path="dashboard" element={<Dashboard />} />
          <Route
            path="director/dashboard"
            element={
              <PurchasePriceRoute fallback={<Dashboard />}>
                <DirectorDashboardPage />
              </PurchasePriceRoute>
            }
          />
          <Route path="accountant/dashboard" element={<AccountantDashboardPage />} />
          <Route path="regional/dashboard" element={<RegionalDashboardPage />} />
          <Route path="orders" element={<OrdersPage />} />
          <Route path="orders/new" element={<OrderFormPage />} />
          <Route path="orders/import" element={<OrderImportPage />} />
          <Route path="orders/:id" element={<OrderDetailPage />} />
          <Route path="visits" element={<VisitsPage />} />
          <Route path="clients" element={<ClientsLayout />}>
            <Route index element={<Navigate to="wholesale" replace />} />
            <Route path="wholesale" element={<ClientsWholesalePage />} />
            <Route path="wholesale/new" element={<ClientFormPage />} />
            <Route path="wholesale/edit/:id" element={<ClientFormPage />} />
            <Route path="wholesale/:id" element={<ClientDetailPage />} />
            <Route path="retail" element={<ClientsRetailPage />} />
          </Route>
          <Route path="tasks" element={<TasksPage />} />
          <Route path="logistics" element={<LogisticsPage />} />

          <Route path="inventory">
            <Route path="products" element={<ProductsPage />} />
            <Route path="products/:id" element={<ProductDetailPage />} />
            <Route path="pricing" element={<PricingPage />} />
            <Route path="locations" element={<LocationsPage />} />
            <Route path="stocks" element={<Navigate to="/warehouse/stocks" replace />} />
          </Route>

          <Route path="finance" element={<FinancePage />} />
          <Route path="finance/installments" element={<InstallmentsPage />} />
          <Route path="finance/debts" element={<DebtsPage />} />

          <Route path="contracts" element={<ContractsPage />} />
          <Route path="contracts/:id" element={<ContractDetailPage />} />

          <Route path="storefront">
            <Route path="dashboard" element={<StorefrontDashboardPage />} />
            <Route path="orders" element={<StorefrontOrdersPage />} />
            <Route path="home" element={<StorefrontHomePage />} />
            <Route path="categories" element={<StorefrontCategoriesPage />} />
            <Route path="product" element={<StorefrontProductPage />} />
            <Route path="pages" element={<StorefrontPagesPage />} />
            <Route path="blog" element={<StorefrontBlogPage />} />
            <Route path="seo" element={<StorefrontSeoPage />} />
            <Route path="settings" element={<StorefrontSettingsPage />} />
            <Route path="podbor" element={<StorefrontPodborPage />} />
          </Route>

          <Route path="hr">
            <Route path="employees" element={<EmployeesPage />} />
            <Route path="shifts" element={<Navigate to="/hr/attendance" replace />} />
            <Route path="attendance" element={<AttendancePage />} />
          </Route>

          <Route path="warehouse" element={<WarehouseLayout />}>
            <Route index element={<Navigate to="stocks" replace />} />
            <Route path="dashboard" element={<WarehouseDashboardPage />} />
            <Route path="stocks" element={<StocksPage />} />
            <Route path="transfers" element={<TransfersPage />} />
            <Route
              path="purchases"
              element={
                <PurchasePriceRoute documentRead>
                  <PurchasesPage />
                </PurchasePriceRoute>
              }
            />
            <Route path="write-offs" element={<WriteOffsPage />} />
            <Route path="adjustments" element={<AdjustmentsPage />} />
            <Route path="returns" element={<ReturnsPage />} />
            <Route path="equipment" element={<ClientEquipmentPage />} />
            <Route path="material" element={<MaterialReportPage />} />
            <Route path="movements/:id" element={<MaterialMovementPage />} />
          </Route>

          <Route path="stores">
            <Route index element={<StoresListPage />} />
            <Route path=":id" element={<StoreDetailPage />} />
          </Route>

          <Route path="reference">
            <Route index element={<ReferencesPage />} />
            <Route path="branches" element={<BranchesPage />} />
            <Route path="currencies" element={<CurrenciesMainPage />} />
            <Route path="accounts" element={<AccountsPage />} />
            <Route path="categories" element={<CategoryPage />} />
            <Route path="brands" element={<BrandsPage />} />
            <Route path="units" element={<UnitsPage />} />
            <Route path="price-types" element={<PriceTypesPage />} />
            <Route path="suppliers" element={<SupplierPage />} />
            <Route path="payment-types" element={<PaymentTypePage />} />
            <Route path="expense-categories" element={<ExpenseCategoryPage />} />
            <Route path="districts" element={<DistrictsPage />} />
            <Route path="car-brands" element={<CarBrandsPage />} />
            <Route path="car-models" element={<CarModelsPage />} />
            <Route path="client-categories" element={<ClientCategoriesPage />} />
            <Route path="refusal-reasons" element={<RefusalReasonsPage />} />
            <Route path="equipment-types" element={<EquipmentTypesPage />} />
          </Route>

          <Route path="analytics">
            <Route index element={<AnalyticsSelectionPage />} />
            <Route path="sales" element={<SalesAnalyticsPage />} />
            <Route path="product-sales" element={<ProductSalesPage />} />
            <Route path="kpi" element={<KpiPage />} />
            <Route path="set-plans" element={<SetKpiPlansPage />} />
            <Route path="builder" element={<ReportBuilderPage />} />
            <Route path="ai" element={<AiChatPage />} />
            <Route path="equipment" element={<Navigate to="/warehouse/equipment" replace />} />
            <Route
              path="pnl"
              element={
                <PurchasePriceRoute>
                  <PnlPage />
                </PurchasePriceRoute>
              }
            />
            <Route path="material" element={<Navigate to="/warehouse/material" replace />} />
            <Route path="slow-moving" element={<SlowMovingGoodsPage />} />
          </Route>

          <Route path="settings/system" element={<SystemSettingsPage />} />
          <Route path="settings/audit-logs" element={<AuditLogPage />} />
          <Route path="settings/profile" element={<ProfileSettingsPage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="video-campaign" element={<VideoCampaignPage />} />

          {/* Agent Specific Routes (Nested to use same layout selection if needed, but pointed to mobile versions) */}
          <Route path="agent">
            <Route path="dashboard" element={<MobileDashboardPage />} />
            <Route path="visits" element={<MobileVisitsPage />} />
            <Route path="visits/:id" element={<MobileVisitDetailPage />} />
            <Route path="orders" element={<MobileOrdersPage />} />
            <Route path="orders/new" element={<MobileNewOrderPage />} />
            <Route path="orders/:id" element={<MobileOrderViewPage />} />
            <Route path="orders/:id/edit" element={<MobileNewOrderPage />} />
            <Route path="clients" element={<MobileClientsPage />} />
            <Route path="clients/:id" element={<MobileClientDetailPage />} />
            <Route path="stocks" element={<MobileStocksPage />} />
            <Route path="analytics" element={<MobileAnalyticsPage />} />
            <Route path="installments" element={<MobileInstallmentsPage />} />
            <Route path="tasks" element={<MobileTasksPage />} />
            <Route path="attendance" element={<MobileAttendancePage />} />
            <Route path="video-campaign" element={<VideoCampaignPage />} />
          </Route>
        </Route>

        {/* POS Route (Outside of MainLayout but inside ProtectedRoute) */}
        <Route
          path="/pos"
          element={
            <ProtectedRoute>
              <POSTerminalLayout />
            </ProtectedRoute>
          }
        >
          <Route path="terminal" element={<POSTerminalPage />} />
          <Route path="" element={<Navigate to="terminal" replace />} />
        </Route>

        <Route path="*" element={<DefaultRedirect />} />
      </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthProvider>
          <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
            <LanguageProvider>
              <SocketProvider>
                <SettingsSync />
                <AppRoutes />
                <CommandPalette />
                <NetworkStatus />
                <AppUpdateNotice />
                <Toaster position="top-right" />
              </SocketProvider>
            </LanguageProvider>
          </ThemeProvider>
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
